/**
 * ADR-220 §3.1 — the device's «قد يعجبك» signal rings (pure: no DOM, no
 * network). Mirrors the app's reco_signals.dart; both are tested against
 * design/reco/signal_cases.json (scripts/tests/eros-reco.test.mjs).
 * Rings stay in this browser; the server gets product gids only — never
 * a search's text.
 */
(function (root) {
  'use strict';

  var VIEW_RING = 30, ADD_RING = 20, SEARCH_RING = 10;
  var VIEW_DAYS = 30, ADD_DAYS = 14, SEARCH_DAYS = 30;
  var WIRE_VIEWS = 12, WIRE_ADDS = 8, WIRE_SEARCHES = 3;
  var SEARCH_TOP = 3, SEARCH_CLICKS = 3;
  var STORE_KEY = 'eros_reco_signals_v1';

  function empty() {
    return { views: [], adds: [], search: [] };
  }

  function recordView(s, gid, now) {
    var views = [{ p: gid, t: now, d: 0 }].concat(s.views.filter(function (v) { return v.p !== gid; }));
    return { views: views.slice(0, VIEW_RING), adds: s.adds, search: s.search };
  }

  function closeView(s, gid, dwell) {
    var changed = false;
    var views = s.views.map(function (v) {
      if (changed || v.p !== gid || dwell <= v.d) return v;
      changed = true;
      return { p: v.p, t: v.t, d: dwell };
    });
    return changed ? { views: views, adds: s.adds, search: s.search } : s;
  }

  function recordAdd(s, gid, now) {
    var adds = [{ p: gid, t: now }].concat(s.adds.filter(function (a) { return a.p !== gid; }));
    return { views: s.views, adds: adds.slice(0, ADD_RING), search: s.search };
  }

  function recordSearch(s, top, now) {
    var seen = {};
    var ids = [];
    (top || []).forEach(function (g) {
      if (!seen[g] && ids.length < SEARCH_TOP) {
        seen[g] = 1;
        ids.push(g);
      }
    });
    if (!ids.length) return s;
    var search = [{ t: now, top: ids, click: [] }].concat(s.search);
    return { views: s.views, adds: s.adds, search: search.slice(0, SEARCH_RING) };
  }

  function recordSearchClick(s, gid) {
    if (!s.search.length) return s;
    var latest = s.search[0];
    if (latest.click.indexOf(gid) >= 0 || latest.click.length >= SEARCH_CLICKS) return s;
    var updated = { t: latest.t, top: latest.top, click: latest.click.concat([gid]) };
    return { views: s.views, adds: s.adds, search: [updated].concat(s.search.slice(1)) };
  }

  /** The `sig` body of POST /v1/reco at `now` (epoch seconds). */
  function toWire(s, now) {
    function fresh(t, days) {
      return now - t <= days * 86400 && t <= now;
    }
    return {
      views: s.views.filter(function (v) { return fresh(v.t, VIEW_DAYS); }).slice(0, WIRE_VIEWS)
        .map(function (v) { return { p: v.p, t: v.t, d: v.d }; }),
      adds: s.adds.filter(function (a) { return fresh(a.t, ADD_DAYS); }).slice(0, WIRE_ADDS)
        .map(function (a) { return { p: a.p, t: a.t }; }),
      search: s.search.filter(function (x) { return fresh(x.t, SEARCH_DAYS); }).slice(0, WIRE_SEARCHES)
        .map(function (x) { return { t: x.t, top: x.top, click: x.click }; })
    };
  }

  /** Stored JSON → rings; anything malformed reads as empty. */
  function parse(raw) {
    var s = empty();
    var data;
    try {
      data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
      return s;
    }
    if (!data || typeof data !== 'object') return s;
    function arr(v) { return Array.isArray(v) ? v : []; }
    function num(v) { return typeof v === 'number' && isFinite(v) ? Math.floor(v) : null; }
    arr(data.views).forEach(function (v) {
      if (v && typeof v.p === 'string' && num(v.t) !== null) s.views.push({ p: v.p, t: num(v.t), d: num(v.d) || 0 });
    });
    arr(data.adds).forEach(function (a) {
      if (a && typeof a.p === 'string' && num(a.t) !== null) s.adds.push({ p: a.p, t: num(a.t) });
    });
    arr(data.search).forEach(function (x) {
      if (x && num(x.t) !== null) {
        s.search.push({
          t: num(x.t),
          top: arr(x.top).filter(function (g) { return typeof g === 'string'; }),
          click: arr(x.click).filter(function (g) { return typeof g === 'string'; })
        });
      }
    });
    s.views = s.views.slice(0, VIEW_RING);
    s.adds = s.adds.slice(0, ADD_RING);
    s.search = s.search.slice(0, SEARCH_RING);
    return s;
  }

  /** A product gid from a numeric id or a gid. */
  function productGid(id) {
    var m = /(\d{1,16})$/.exec(String(id == null ? '' : id));
    return m ? 'gid://shopify/Product/' + m[1] : null;
  }

  /** The Section Rendering query for ranked ids: `id:<n> OR id:<n>`. */
  function searchQuery(gids) {
    return gids
      .map(function (g) { var m = /(\d{1,16})$/.exec(g); return m ? 'id:' + m[1] : null; })
      .filter(Boolean)
      .join(' OR ');
  }

  /* ---------- opened recommendations (ADR-220 §6.1, order attribute eros_reco) ---------- */
  var HITS_KEY = 'eros_reco_hits_v1';
  var HIT_KEEP = 7 * 86400;
  var HIT_MAX = 20;

  function recordHit(hits, hit, now) {
    var out = [hit].concat((hits || []).filter(function (h) {
      return h && h.p !== hit.p && now - h.t <= HIT_KEEP;
    }));
    return out.slice(0, HIT_MAX);
  }

  /** `recoHits` for /v1/checkout/confirm: fresh hits whose product is in the cart, ≤ 10. */
  function hitsForCheckout(hits, cartProductGids, now) {
    return (hits || [])
      .filter(function (h) { return h && now - h.t <= HIT_KEEP && cartProductGids.indexOf(h.p) >= 0; })
      .slice(0, 10)
      .map(function (h) { return { p: h.p, s: h.s, v: h.v }; });
  }

  /**
   * Sign-out purge trigger. ErosSession.onChange calls back at once with the
   * current state, so only a real signed-in → signed-out transition counts:
   * a signed-out visitor must keep their own rings across page loads.
   */
  function isSignOut(wasSignedIn, session) {
    return wasSignedIn === true && !(session && session.customerGid);
  }

  var api = {
    STORE_KEY: STORE_KEY,
    isSignOut: isSignOut,
    HITS_KEY: HITS_KEY,
    recordHit: recordHit,
    hitsForCheckout: hitsForCheckout,
    empty: empty,
    recordView: recordView,
    closeView: closeView,
    recordAdd: recordAdd,
    recordSearch: recordSearch,
    recordSearchClick: recordSearchClick,
    toWire: toWire,
    parse: parse,
    productGid: productGid,
    searchQuery: searchQuery
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.ErosRecoRules = api;
})(typeof window !== 'undefined' ? window : globalThis);
